import React, { useRef, useEffect, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Float } from '@react-three/drei';
import * as THREE from 'three';

/* -------------------------------------------------------
   Shared constants
   ------------------------------------------------------- */
const SLIDE_SPACING = 8;

/* -------------------------------------------------------
   Slide 0 — Computer / PC  (Inteligencia Artificial)
   Monitor + tower + keyboard + mouse + floating data particles
   ------------------------------------------------------- */
function AIObject() {
  const groupRef = useRef();
  const screenRef = useRef();

  // "Code lines" on screen — horizontal line segments
  const codeGeo = useMemo(() => {
    const lines = [];
    for (let row = 0; row < 6; row++) {
      const y = 0.95 - row * 0.13;
      const w = 0.25 + Math.random() * 0.7;
      const x0 = -0.55;
      lines.push(x0, y, 0.045, x0 + w, y, 0.045);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(lines), 3));
    return geo;
  }, []);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (groupRef.current) groupRef.current.rotation.y = t * 0.1;
    if (screenRef.current) {
      screenRef.current.material.opacity = 0.08 + Math.sin(t * 1.5) * 0.03;
    }
  });

  return (
    <Float speed={1.5} rotationIntensity={0.2} floatIntensity={0.4}>
      <group ref={groupRef} scale={1.15}>
        {/* Monitor frame */}
        <mesh position={[0, 0.65, 0]}>
          <boxGeometry args={[1.5, 1.0, 0.06]} />
          <meshBasicMaterial color="#fff" wireframe transparent opacity={0.3} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        {/* Screen glow */}
        <mesh ref={screenRef} position={[0, 0.65, 0.04]}>
          <planeGeometry args={[1.3, 0.85]} />
          <meshBasicMaterial color="#fff" transparent opacity={0.08} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} />
        </mesh>
        {/* Code lines on screen */}
        <lineSegments geometry={codeGeo}>
          <lineBasicMaterial color="#fff" transparent opacity={0.12} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
        {/* Stand neck */}
        <mesh position={[0, 0.08, 0]}>
          <cylinderGeometry args={[0.03, 0.04, 0.25, 6]} />
          <meshBasicMaterial color="#fff" wireframe transparent opacity={0.22} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        {/* Stand base */}
        <mesh position={[0, -0.06, 0]}>
          <boxGeometry args={[0.5, 0.03, 0.3]} />
          <meshBasicMaterial color="#fff" wireframe transparent opacity={0.22} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        {/* Tower / CPU */}
        <mesh position={[-1.05, 0.2, 0]}>
          <boxGeometry args={[0.38, 0.85, 0.28]} />
          <meshBasicMaterial color="#fff" wireframe transparent opacity={0.22} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        {/* Tower ventilation slits */}
        {[0, 1, 2].map((j) => (
          <mesh key={`vent-${j}`} position={[-1.05, 0.48 - j * 0.13, 0.15]}>
            <boxGeometry args={[0.22, 0.012, 0.003]} />
            <meshBasicMaterial color="#fff" transparent opacity={0.15} blending={THREE.AdditiveBlending} depthWrite={false} />
          </mesh>
        ))}
        {/* Power LED */}
        <mesh position={[-0.88, 0.5, 0.15]}>
          <sphereGeometry args={[0.02, 8, 8]} />
          <meshBasicMaterial color="#fff" transparent opacity={0.65} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        {/* Keyboard */}
        <mesh position={[0.05, -0.22, 0.45]} rotation={[-0.12, 0, 0]}>
          <boxGeometry args={[1.0, 0.03, 0.35]} />
          <meshBasicMaterial color="#fff" wireframe transparent opacity={0.18} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        {/* Mouse */}
        <mesh position={[0.72, -0.22, 0.48]}>
          <boxGeometry args={[0.12, 0.03, 0.18]} />
          <meshBasicMaterial color="#fff" wireframe transparent opacity={0.18} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        {/* Cable tower → monitor */}
        <lineSegments>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={4}
              array={new Float32Array([
                -0.86, 0.2, 0,  -0.75, 0.2, 0,
                -0.75, 0.2, 0,  -0.75, 0.65, 0,
              ])}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color="#fff" transparent opacity={0.12} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
      </group>
    </Float>
  );
}

/* -------------------------------------------------------
   Slide 1 — Shipping Box  (Logística)
   Cardboard box wireframe + tape seam + shipping label
   + barcode + arrow symbol + location pin
   ------------------------------------------------------- */
function LogisticsObject() {
  const groupRef = useRef();
  const labelGlowRef = useRef();

  // Main box — clean edges
  const boxGeo = useMemo(() => new THREE.EdgesGeometry(new THREE.BoxGeometry(1.4, 1.0, 1.0)), []);

  // Shipping label rectangle on front face
  const labelGeo = useMemo(() => new THREE.EdgesGeometry(new THREE.BoxGeometry(0.7, 0.45, 0.005)), []);

  // Box details: tape seam, flap lines, tape side strips
  const detailsGeo = useMemo(() => {
    const lines = [
      // Top center seam (where flaps meet)
      -0.7, 0.5, 0,  0.7, 0.5, 0,
      // Tape across top seam (slightly wider, perpendicular)
       0, 0.501, -0.5,   0, 0.501, 0.5,
      // Top flap fold lines (left & right halves)
      -0.7, 0.5, -0.5,  -0.7, 0.5, 0.5,
       0.7, 0.5, -0.5,   0.7, 0.5, 0.5,
      // Bottom seam
      -0.7, -0.5, 0,  0.7, -0.5, 0,
      // Bottom flap fold lines
      -0.7, -0.5, -0.5,  -0.7, -0.5, 0.5,
       0.7, -0.5, -0.5,   0.7, -0.5, 0.5,
      // Tape side strips on front face (vertical along edges)
      -0.7, 0.5, 0.501,  -0.7, 0.25, 0.501,
       0.7, 0.5, 0.501,   0.7, 0.25, 0.501,
      // Tape side strips on back face
      -0.7, 0.5, -0.501,  -0.7, 0.25, -0.501,
       0.7, 0.5, -0.501,   0.7, 0.25, -0.501,
    ];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(lines), 3));
    return geo;
  }, []);

  // Barcode lines on the shipping label
  const barcodeGeo = useMemo(() => {
    const lines = [];
    const y0 = -0.18, y1 = -0.02;
    const z = 0.505;
    const widths = [0.012, 0.006, 0.012, 0.006, 0.012, 0.006, 0.012, 0.006, 0.012, 0.006, 0.012, 0.006, 0.012];
    let x = -0.2;
    widths.forEach((w) => {
      lines.push(x, y0, z, x, y1, z);
      x += w + 0.008;
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(lines), 3));
    return geo;
  }, []);

  // "Address" text lines on label (horizontal lines simulating text)
  const addressGeo = useMemo(() => {
    const lines = [];
    const z = 0.505;
    for (let i = 0; i < 3; i++) {
      const y = 0.12 - i * 0.07;
      const w = 0.45 - i * 0.08;
      lines.push(-0.28, y, z, -0.28 + w, y, z);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(lines), 3));
    return geo;
  }, []);

  // Arrow ↑ "THIS SIDE UP" on right side face
  const arrowGeo = useMemo(() => {
    const x = 0.701;
    const lines = [
      // Arrow shaft
      x, -0.15, 0,  x, 0.2, 0,
      // Arrow head left
      x, 0.2, 0,  x, 0.1, -0.1,
      // Arrow head right
      x, 0.2, 0,  x, 0.1, 0.1,
    ];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(lines), 3));
    return geo;
  }, []);

  // Location pin on left side face
  const pinGeo = useMemo(() => {
    const x = -0.701;
    const lines = [];
    // Pin circle (octagon)
    const r = 0.08;
    const cy = 0.1;
    for (let i = 0; i < 8; i++) {
      const a1 = (i / 8) * Math.PI * 2;
      const a2 = ((i + 1) / 8) * Math.PI * 2;
      lines.push(
        x, cy + Math.sin(a1) * r, Math.cos(a1) * r,
        x, cy + Math.sin(a2) * r, Math.cos(a2) * r
      );
    }
    // Pin point (triangle bottom)
    lines.push(x, cy - r, -0.05, x, cy - r * 2.2, 0);
    lines.push(x, cy - r, 0.05, x, cy - r * 2.2, 0);
    // Inner dot
    const ri = 0.03;
    for (let i = 0; i < 6; i++) {
      const a1 = (i / 6) * Math.PI * 2;
      const a2 = ((i + 1) / 6) * Math.PI * 2;
      lines.push(
        x, cy + Math.sin(a1) * ri, Math.cos(a1) * ri,
        x, cy + Math.sin(a2) * ri, Math.cos(a2) * ri
      );
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(lines), 3));
    return geo;
  }, []);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (groupRef.current) groupRef.current.rotation.y = t * 0.1;
    if (labelGlowRef.current) {
      labelGlowRef.current.material.opacity = 0.06 + Math.sin(t * 1.5) * 0.02;
    }
  });

  return (
    <Float speed={1.2} rotationIntensity={0.2} floatIntensity={0.4}>
      <group ref={groupRef} scale={1.1}>
        {/* Main box wireframe */}
        <lineSegments geometry={boxGeo}>
          <lineBasicMaterial color="#fff" transparent opacity={0.35} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
        {/* Box details (seams, tape, flap folds) */}
        <lineSegments geometry={detailsGeo}>
          <lineBasicMaterial color="#fff" transparent opacity={0.18} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
        {/* Shipping label outline */}
        <lineSegments geometry={labelGeo} position={[0.05, 0.0, 0.503]}>
          <lineBasicMaterial color="#fff" transparent opacity={0.3} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
        {/* Label glow */}
        <mesh ref={labelGlowRef} position={[0.05, 0.0, 0.504]}>
          <planeGeometry args={[0.7, 0.45]} />
          <meshBasicMaterial color="#fff" transparent opacity={0.06} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} />
        </mesh>
        {/* Address lines on label */}
        <lineSegments geometry={addressGeo} position={[0.05, 0, 0]}>
          <lineBasicMaterial color="#fff" transparent opacity={0.12} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
        {/* Barcode on label */}
        <lineSegments geometry={barcodeGeo} position={[0.05, 0, 0]}>
          <lineBasicMaterial color="#fff" transparent opacity={0.15} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
        {/* Arrow ↑ on right side */}
        <lineSegments geometry={arrowGeo}>
          <lineBasicMaterial color="#fff" transparent opacity={0.2} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
        {/* Location pin on left side */}
        <lineSegments geometry={pinGeo}>
          <lineBasicMaterial color="#fff" transparent opacity={0.2} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
      </group>
    </Float>
  );
}

/* -------------------------------------------------------
   Slide 2 — Lightning Bolt  (Energía)
   Zigzag bolt + branches + glow nodes + sparks
   ------------------------------------------------------- */
function EnergyObject() {
  const groupRef = useRef();
  const nodesRef = useRef();

  // Main bolt + branches as line-segment pairs
  const boltGeo = useMemo(() => {
    const main = [
       0.1,  1.8,  0,     -0.35, 1.15,  0.05,
      -0.35, 1.15,  0.05,  0.2,  0.65, -0.03,
       0.2,  0.65, -0.03, -0.3,  0.1,   0.04,
      -0.3,  0.1,   0.04,  0.2, -0.4,  -0.02,
       0.2, -0.4,  -0.02, -0.15,-1.0,   0.03,
      -0.15,-1.0,   0.03,  0.05,-1.6,   0,
    ];
    const branch1 = [
      -0.3,  0.1,  0.04,  0.55, -0.1,  0.15,
       0.55,-0.1,  0.15,  0.4,  -0.45, 0.1,
    ];
    const branch2 = [
       0.2,  0.65,-0.03, -0.65, 0.4, -0.15,
      -0.65, 0.4, -0.15, -0.5,  0.1, -0.1,
    ];
    const branch3 = [
       0.2, -0.4, -0.02,  0.65, -0.55, -0.1,
    ];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(
      new Float32Array([...main, ...branch1, ...branch2, ...branch3]), 3
    ));
    return geo;
  }, []);

  // Glowing nodes at bolt joints
  const nodePositions = useMemo(() => {
    const pts = new Float32Array([
      0.1, 1.8, 0,      -0.35, 1.15, 0.05,
      0.2, 0.65, -0.03,  -0.3, 0.1, 0.04,
      0.2, -0.4, -0.02,  -0.15, -1.0, 0.03,
      0.05, -1.6, 0,      0.55, -0.1, 0.15,
      -0.65, 0.4, -0.15,  0.65, -0.55, -0.1,
    ]);
    return { array: pts, count: pts.length / 3 };
  }, []);

  // Spark particles
  const sparks = useMemo(() => {
    const count = 30;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3]     = (Math.random() - 0.5) * 1.8;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 3.8;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 0.8;
    }
    return { pos, count };
  }, []);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (groupRef.current) groupRef.current.rotation.y = t * 0.15;
    if (nodesRef.current) {
      nodesRef.current.material.opacity = 0.5 + Math.sin(t * 3) * 0.25;
    }
  });

  return (
    <Float speed={1.3} rotationIntensity={0.25} floatIntensity={0.45}>
      <group ref={groupRef} scale={1.05}>
        {/* Lightning bolt lines */}
        <lineSegments geometry={boltGeo}>
          <lineBasicMaterial color="#fff" transparent opacity={0.5} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
        {/* Glow nodes at joints */}
        <points ref={nodesRef}>
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" count={nodePositions.count} array={nodePositions.array} itemSize={3} />
          </bufferGeometry>
          <pointsMaterial color="#fff" size={0.12} transparent opacity={0.6} blending={THREE.AdditiveBlending} depthWrite={false} sizeAttenuation />
        </points>
        {/* Source sphere (top) */}
        <mesh position={[0.1, 1.8, 0]}>
          <sphereGeometry args={[0.18, 12, 12]} />
          <meshBasicMaterial color="#fff" transparent opacity={0.15} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        {/* Impact glow (bottom) */}
        <mesh position={[0.05, -1.6, 0]}>
          <sphereGeometry args={[0.22, 12, 12]} />
          <meshBasicMaterial color="#fff" transparent opacity={0.12} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        {/* Electric ring around bolt mid-section */}
        <mesh position={[0, 0.1, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.5, 0.01, 8, 24]} />
          <meshBasicMaterial color="#fff" wireframe transparent opacity={0.1} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        {/* Spark particles */}
        <points>
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" count={sparks.count} array={sparks.pos} itemSize={3} />
          </bufferGeometry>
          <pointsMaterial color="#fff" size={0.03} transparent opacity={0.3} blending={THREE.AdditiveBlending} depthWrite={false} sizeAttenuation />
        </points>
      </group>
    </Float>
  );
}

/* -------------------------------------------------------
   Slide 3 — Shield + Padlock  (Seguridad Ciberfísica)
   Extruded shield shape + padlock + circuit lines
   ------------------------------------------------------- */
function SecurityObject() {
  const groupRef = useRef();
  const shieldEdgeRef = useRef();

  // Shield outline → extruded → edges
  const shieldEdgesGeo = useMemo(() => {
    const shape = new THREE.Shape();
    shape.moveTo(0, 1.4);
    shape.lineTo(0.95, 0.95);
    shape.lineTo(1.05, 0.2);
    shape.lineTo(0.95, -0.25);
    shape.quadraticCurveTo(0.55, -1.05, 0, -1.4);
    shape.quadraticCurveTo(-0.55, -1.05, -0.95, -0.25);
    shape.lineTo(-1.05, 0.2);
    shape.lineTo(-0.95, 0.95);
    shape.closePath();
    const extruded = new THREE.ExtrudeGeometry(shape, { depth: 0.12, bevelEnabled: false });
    return new THREE.EdgesGeometry(extruded);
  }, []);

  // Padlock body → edges
  const padlockGeo = useMemo(() => new THREE.EdgesGeometry(new THREE.BoxGeometry(0.35, 0.38, 0.18)), []);

  // Padlock shackle — manual arc (avoids EdgesGeometry cap lines on cut torus)
  const shackleGeo = useMemo(() => {
    const lines = [];
    const r = 0.12;
    const segs = 12;
    for (let i = 0; i < segs; i++) {
      const a1 = Math.PI * (i / segs);
      const a2 = Math.PI * ((i + 1) / segs);
      lines.push(
        Math.cos(a1) * r, Math.sin(a1) * r, 0,
        Math.cos(a2) * r, Math.sin(a2) * r, 0
      );
    }
    lines.push(-r, 0, 0, -r, -0.1, 0);
    lines.push(r, 0, 0, r, -0.1, 0);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(lines), 3));
    return geo;
  }, []);

  // Circuit pattern lines on shield face
  const circuitGeo = useMemo(() => {
    const lines = [
      // Horizontal
      -0.55, 0.55, 0.13,   0.55, 0.55, 0.13,
      -0.45, 0.1,  0.13,   0.45, 0.1,  0.13,
      -0.35,-0.35, 0.13,   0.35,-0.35, 0.13,
      // Vertical connectors
       0.3, 0.55, 0.13,   0.3,  0.1,  0.13,
      -0.3, 0.1,  0.13,  -0.3, -0.35, 0.13,
       0.0, 0.55, 0.13,   0.0, -0.35, 0.13,
      // Diagonal accents
       0.45, 0.1, 0.13,   0.55,-0.1, 0.13,
      -0.45, 0.1, 0.13,  -0.55,-0.1, 0.13,
    ];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(lines), 3));
    return geo;
  }, []);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (groupRef.current) groupRef.current.rotation.y = t * 0.12;
    if (shieldEdgeRef.current) {
      shieldEdgeRef.current.material.opacity = 0.28 + Math.sin(t * 1.5) * 0.1;
    }
  });

  return (
    <Float speed={1.4} rotationIntensity={0.3} floatIntensity={0.5}>
      <group ref={groupRef} scale={0.95}>
        {/* Shield wireframe */}
        <lineSegments ref={shieldEdgeRef} geometry={shieldEdgesGeo}>
          <lineBasicMaterial color="#fff" transparent opacity={0.3} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
        {/* Circuit lines on face */}
        <lineSegments geometry={circuitGeo}>
          <lineBasicMaterial color="#fff" transparent opacity={0.12} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
        {/* Padlock body */}
        <lineSegments geometry={padlockGeo} position={[0, -0.05, 0.14]}>
          <lineBasicMaterial color="#fff" transparent opacity={0.3} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
        {/* Padlock shackle */}
        <lineSegments geometry={shackleGeo} position={[0, 0.14, 0.14]} rotation={[0, 0, 0]}>
          <lineBasicMaterial color="#fff" transparent opacity={0.3} blending={THREE.AdditiveBlending} depthWrite={false} />
        </lineSegments>
        {/* Keyhole dot */}
        <mesh position={[0, -0.1, 0.24]}>
          <circleGeometry args={[0.035, 10]} />
          <meshBasicMaterial color="#fff" transparent opacity={0.35} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        {/* Keyhole slot */}
        <mesh position={[0, -0.15, 0.24]}>
          <planeGeometry args={[0.02, 0.06]} />
          <meshBasicMaterial color="#fff" transparent opacity={0.25} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} />
        </mesh>
        {/* Shield vertex glow */}
        <points>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={shieldEdgesGeo.attributes.position.count}
              array={shieldEdgesGeo.attributes.position.array}
              itemSize={3}
            />
          </bufferGeometry>
          <pointsMaterial color="#fff" size={0.03} transparent opacity={0.25} blending={THREE.AdditiveBlending} depthWrite={false} sizeAttenuation />
        </points>
      </group>
    </Float>
  );
}

/* -------------------------------------------------------
   Scene — positions all 4 objects and slides them
   Panel 0 = landing (no 3D), panels 1-4 map to objects 0-3
   ------------------------------------------------------- */
const OBJECTS = [AIObject, LogisticsObject, EnergyObject, SecurityObject];

function CarouselScene({ activePanel, transitionRef }) {
  const groupRef = useRef();
  const { camera } = useThree();
  const mouseTarget = useRef({ x: 0, y: 0 });
  const mouseSmooth = useRef({ x: 0, y: 0 });

  // Track mouse at window level (canvas has pointer-events: none)
  useEffect(() => {
    const onMouseMove = (e) => {
      mouseTarget.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouseTarget.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMouseMove);
  }, []);

  useFrame(() => {
    if (!groupRef.current || !transitionRef.current) return;

    // Smooth mouse lerp
    mouseSmooth.current.x += (mouseTarget.current.x - mouseSmooth.current.x) * 0.06;
    mouseSmooth.current.y += (mouseTarget.current.y - mouseSmooth.current.y) * 0.06;

    const { fromPanel, toPanel, progress } = transitionRef.current;

    const panelToX = (p) => -(p - 1) * SLIDE_SPACING;

    let targetX;
    if (progress < 1) {
      const fromX = panelToX(fromPanel);
      const toX = panelToX(toPanel);
      targetX = fromX + (toX - fromX) * progress;
    } else {
      targetX = panelToX(toPanel);
    }

    groupRef.current.position.x = targetX;

    // Mouse-driven camera perspective shift
    camera.position.x = mouseSmooth.current.x * 1.0;
    camera.position.y = -mouseSmooth.current.y * 1.2;
    camera.position.z = 5.5;
    camera.lookAt(0, 0, 0);
  });

  return (
    <group ref={groupRef}>
      {OBJECTS.map((ObjectComponent, i) => {
        const sideOffset = i % 2 === 0 ? -1.8 : 1.8;
        return (
          <group key={i} position={[i * SLIDE_SPACING + sideOffset, 0, 0]}>
            <ObjectComponent />
          </group>
        );
      })}
    </group>
  );
}

/* -------------------------------------------------------
   Exported Canvas wrapper
   ------------------------------------------------------- */
export default function HeroCarousel3D({ activePanel, transitionRef }) {
  return (
    <div className="hero-canvas-container">
      <Canvas
        camera={{ position: [0, 0, 5.5], fov: 50 }}
        dpr={[1, 1.5]}
        gl={{
          antialias: true,
          alpha: true,
          powerPreference: 'high-performance',
        }}
        style={{ background: 'transparent' }}
      >
        <CarouselScene activePanel={activePanel} transitionRef={transitionRef} />
      </Canvas>
    </div>
  );
}
